import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, AlertTriangle, CheckCircle, Phone, Clock, Award, Users } from 'lucide-react';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: <FileText className="h-8 w-8 text-blue-500" />,
      title: "Service Agreement",
      content: [
        "These terms govern the use of Jara Safety Nets services and website",
        "By using our services, you agree to be bound by these terms",
        "We reserve the right to modify these terms at any time",
        "Continued use constitutes acceptance of updated terms"
      ]
    },
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Safety Net Installation Services",
      content: [
        "Professional installation of safety nets and protective systems",
        "Compliance with local safety regulations and standards",
        "Quality materials and certified installation procedures",
        "Warranty coverage as specified in service agreements"
      ]
    },
    {
      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
      title: "Limitations and Disclaimers",
      content: [
        "Safety nets are protective measures, not absolute guarantees",
        "Regular inspection and maintenance are customer responsibilities",
        "We are not liable for misuse or improper maintenance",
        "Force majeure events may affect service delivery"
      ]
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-purple-500" />,
      title: "Customer Responsibilities",
      content: [
        "Provide accurate site information and access",
        "Follow maintenance guidelines provided by our team",
        "Report any issues or damage promptly",
        "Comply with local building and safety codes"
      ]
    },
    {
      icon: <Users className="h-8 w-8 text-red-500" />,
      title: "Payment and Billing",
      content: [
        "Payment terms as agreed in service contracts",
        "Late payment fees may apply as specified",
        "Refund policy applies to unused services only",
        "Price changes will be communicated in advance"
      ]
    },
    {
      icon: <Award className="h-8 w-8 text-indigo-500" />,
      title: "Warranty and Support",
      content: [
        "Manufacturer warranty on materials and workmanship",
        "Free maintenance for specified warranty period",
        "Emergency repair services available",
        "Customer support during business hours"
      ]
    }
  ];

  const additionalTerms = [
    {
      title: "Intellectual Property",
      content: "All website content, designs, and materials are proprietary to Jara Safety Nets. Unauthorized use is prohibited."
    },
    {
      title: "Privacy and Data Protection",
      content: "We collect and use personal information in accordance with our Privacy Policy. Customer data is protected and not shared with third parties without consent."
    },
    {
      title: "Force Majeure",
      content: "We are not liable for delays or failures due to circumstances beyond our control, including natural disasters, government actions, or supply chain disruptions."
    },
    {
      title: "Dispute Resolution",
      content: "Any disputes will be resolved through negotiation first, then through binding arbitration in Hyderabad, Telangana jurisdiction."
    },
    {
      title: "Governing Law",
      content: "These terms are governed by the laws of India and the state of Telangana. Any legal proceedings will be conducted in Hyderabad courts."
    },
    {
      title: "Severability",
      content: "If any provision of these terms is found invalid, the remaining provisions will continue to be valid and enforceable."
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('/images/building-nets.webp')`
        }}></div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gray-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Please read these terms carefully before using our safety net services and website.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-gray-300 mt-4"
          >
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Welcome to Jara Safety Nets
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              These Terms of Service ("Terms") govern your use of our website and safety net installation services. 
              By accessing our website or engaging our services, you agree to be bound by these terms and conditions.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Jara Safety Nets is a professional safety net installation company based in Karmanghat, Hyderabad, 
              serving Andhra Pradesh and Telangana with comprehensive safety solutions.
            </p>
          </motion.div>

          {/* Main Terms Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center space-x-3 mb-4">
                  {section.icon}
                  <h3 className="text-xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Additional Terms */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Additional Terms and Conditions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {additionalTerms.map((term, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {term.title}
                  </h4>
                  <p className="text-gray-600">
                    {term.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Service Areas and Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-blue-50 rounded-xl p-8 mb-12"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Service Areas and Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Service Areas</h4>
                <p className="text-gray-600 mb-4">
                  We provide safety net installation services across:
                </p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Hyderabad, Telangana</li>
                  <li>• Vijayawada, Krishna District</li>
                  <li>• Guntur, Visakhapatnam</li>
                  <li>• Tirupati, Rajahmundry</li>
                  <li>• Warangal, Nizamabad</li>
                  <li>• And surrounding areas</li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
                <div className="space-y-3 text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-yellow-500" />
                    <span>+91 8466 055 207</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    <span>Mon-Sat: 9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="h-4 w-4 text-yellow-500" />
                    <span>BIS Certified Installation</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Important Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-12"
          >
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Important Notice
                </h4>
                <p className="text-gray-700">
                  Safety nets are protective measures designed to reduce risk, but they do not eliminate all hazards. 
                  Proper installation, regular inspection, and maintenance are essential for optimal performance. 
                  Customers should always follow safety guidelines and local regulations.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact for Questions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-8 text-center text-white"
          >
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Questions About Our Terms?
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              If you have any questions about these Terms of Service or our safety net services, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@jarasafetynets.com"
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <FileText className="mr-2 h-5 w-5" />
                Email Us
              </a>
              <a
                href="tel:+918466055207"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
